import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Reorder, useDragControls } from "motion/react";
import {
  ChevronDown,
  ChevronUp,
  Copy,
  ExternalLink,
  GitBranch,
  GripVertical,
  Phone,
  PhoneCall,
  Plus,
  Reply,
  Trash2,
  UserRound,
} from "lucide-react";
import { isValidWhatsAppTemplatePhoneNumber } from "@multi-chat/shared";
import { Button } from "../../../../components/ui/button/index.js";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../../../components/ui/popover/index.js";
import { cn } from "../../../../lib/utils.js";
import {
  duplicateQuickReplyIndexes,
  isDynamicTemplateUrl,
  isValidTemplateWebsiteUrl,
  moveGroupedButton,
  partitionTemplateButtons,
  reorderGroupByClientIds,
  TEMPLATE_LIMITS,
  toDynamicTemplateUrl,
  toStaticTemplateUrl,
  VOICE_CALL_DURATION_DAYS_MAX,
  VOICE_CALL_DURATION_DAYS_MIN,
  VOICE_CALL_TTL_MINUTES_DEFAULT,
  voiceCallDaysToTtlMinutes,
  voiceCallTtlMinutesToDays,
  WHATSAPP_CALLING_DOCS_URL,
} from "../../template-editor-presentation.js";
import {
  createTemplateFormButtonClientId,
  type TemplateButtonFormState,
  type TemplateFormState,
} from "../template-management-form.js";
import { WizardPhoneInput } from "./WizardPhoneInput.js";
import { WizardRadioSelect } from "./WizardRadioSelect.js";

const ADD_BUTTON_TYPES = [
  "QUICK_REPLY",
  "URL",
  "VOICE_CALL",
  "PHONE_NUMBER",
  "FLOW",
  "COPY_CODE",
  "SHARE_CONTACT",
] as const;

type AddButtonType = (typeof ADD_BUTTON_TYPES)[number];

const ADD_BUTTON_ICONS: Record<AddButtonType, typeof Reply> = {
  QUICK_REPLY: Reply,
  URL: ExternalLink,
  VOICE_CALL: PhoneCall,
  PHONE_NUMBER: Phone,
  FLOW: GitBranch,
  COPY_CODE: Copy,
  SHARE_CONTACT: UserRound,
};

const VOICE_CALL_DURATION_OPTIONS = Array.from(
  { length: VOICE_CALL_DURATION_DAYS_MAX - VOICE_CALL_DURATION_DAYS_MIN + 1 },
  (_, index) => String(index + VOICE_CALL_DURATION_DAYS_MIN),
);

function isSupportedAddType(
  type: AddButtonType,
  category: TemplateFormState["category"],
): type is TemplateButtonFormState["type"] {
  // Meta utility templates document Quick-reply, URL, Phone, Call request — not VOICE_CALL.
  if (type === "VOICE_CALL" && category === "UTILITY") return false;
  return (
    type === "QUICK_REPLY" ||
    type === "URL" ||
    type === "PHONE_NUMBER" ||
    type === "VOICE_CALL"
  );
}

function buttonClientId(button: TemplateButtonFormState, fallback: string): string {
  return button.clientId ?? fallback;
}

function FieldCounterInput({
  value,
  onChange,
  maxLength,
  placeholder,
  invalid,
  dir,
}: {
  value: string;
  onChange: (value: string) => void;
  maxLength: number;
  placeholder?: string;
  invalid?: boolean;
  dir?: "ltr";
}): React.JSX.Element {
  return (
    <div className="relative" dir={dir}>
      <input
        dir={dir}
        className={cn(
          "h-10 w-full rounded-md border bg-surface px-3 pe-14 text-sm text-ink",
          dir === "ltr" && "[unicode-bidi:isolate]",
          invalid ? "border-error" : "border-line",
        )}
        value={value}
        maxLength={maxLength}
        placeholder={placeholder}
        onChange={(event) => onChange(event.target.value)}
      />
      <span className="pointer-events-none absolute end-3 top-1/2 -translate-y-1/2 text-[0.7rem] text-ink-muted">
        {value.length}/{maxLength}
      </span>
    </div>
  );
}

function DragHandle({
  onPointerDown,
}: {
  onPointerDown: (event: React.PointerEvent) => void;
}): React.JSX.Element {
  return (
    <button
      type="button"
      className="cursor-grab touch-none text-ink-muted hover:text-ink active:cursor-grabbing"
      onPointerDown={onPointerDown}
      aria-label="Drag to reorder"
    >
      <GripVertical size={16} />
    </button>
  );
}

function RowChrome({
  onPointerDown,
  onMoveUp,
  onMoveDown,
  onRemove,
  canMoveUp,
  canMoveDown,
  removeLabel,
  moveUpLabel,
  moveDownLabel,
  children,
}: {
  onPointerDown: (event: React.PointerEvent) => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onRemove: () => void;
  canMoveUp: boolean;
  canMoveDown: boolean;
  removeLabel: string;
  moveUpLabel: string;
  moveDownLabel: string;
  children?: React.ReactNode;
}): React.JSX.Element {
  return (
    <div className="mb-2 flex items-center gap-1">
      <DragHandle onPointerDown={onPointerDown} />
      <button
        type="button"
        className="rounded p-1 text-ink-muted hover:text-ink disabled:opacity-30"
        onClick={onMoveUp}
        disabled={!canMoveUp}
        aria-label={moveUpLabel}
      >
        <ChevronUp size={14} />
      </button>
      <button
        type="button"
        className="rounded p-1 text-ink-muted hover:text-ink disabled:opacity-30"
        onClick={onMoveDown}
        disabled={!canMoveDown}
        aria-label={moveDownLabel}
      >
        <ChevronDown size={14} />
      </button>
      {children}
      <span className="flex-1" />
      <button
        type="button"
        className="rounded p-1 text-ink-muted hover:text-error"
        onClick={onRemove}
        aria-label={removeLabel}
      >
        <Trash2 size={14} />
      </button>
    </div>
  );
}

function QuickReplyRow({
  button,
  itemId,
  duplicate,
  canMoveUp,
  canMoveDown,
  onUpdate,
  onRemove,
  onMoveUp,
  onMoveDown,
}: {
  button: Extract<TemplateButtonFormState, { type: "QUICK_REPLY" }>;
  itemId: string;
  duplicate: boolean;
  canMoveUp: boolean;
  canMoveDown: boolean;
  onUpdate: (button: TemplateButtonFormState) => void;
  onRemove: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const controls = useDragControls();
  const kind =
    button.text === "Preconfigured Response" ? "PRECONFIGURED" : "CUSTOM";

  return (
    <Reorder.Item
      value={itemId}
      dragControls={controls}
      dragListener={false}
      className="rounded-md border border-line bg-surface p-3"
      layout
      transition={{ layout: { type: "spring", bounce: 0.12, visualDuration: 0.22 } }}
    >
      <RowChrome
        onPointerDown={(event) => controls.start(event)}
        onMoveUp={onMoveUp}
        onMoveDown={onMoveDown}
        onRemove={onRemove}
        canMoveUp={canMoveUp}
        canMoveDown={canMoveDown}
        removeLabel={t("whatsapp.templates.manage.wizard.editor.buttonRemove")}
        moveUpLabel={t("whatsapp.templates.manage.wizard.editor.buttonMoveUp")}
        moveDownLabel={t("whatsapp.templates.manage.wizard.editor.buttonMoveDown")}
      >
        <span className="text-xs font-medium text-ink-muted">
          {t("whatsapp.templates.manage.wizard.editor.buttonTypeLabel")}
        </span>
      </RowChrome>
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="grid gap-1 text-xs font-medium text-ink">
          {t("whatsapp.templates.manage.wizard.editor.buttonTypeLabel")}
          <WizardRadioSelect
            ariaLabel={t("whatsapp.templates.manage.wizard.editor.buttonTypeLabel")}
            value={kind}
            onChange={(next) => {
              onUpdate({
                ...button,
                text:
                  next === "PRECONFIGURED"
                    ? "Preconfigured Response"
                    : "Quick Reply",
              });
            }}
            options={[
              {
                value: "CUSTOM",
                label: t("whatsapp.templates.manage.wizard.editor.buttonTypes.QUICK_REPLY"),
              },
              {
                value: "PRECONFIGURED",
                label: t("whatsapp.templates.manage.wizard.editor.buttonPreconfigured"),
              },
            ]}
          />
        </label>
        <label className="grid gap-1 text-xs font-medium text-ink">
          {t("whatsapp.templates.manage.wizard.editor.buttonUrlText")}
          <FieldCounterInput
            value={button.text}
            onChange={(text) => onUpdate({ ...button, text })}
            maxLength={TEMPLATE_LIMITS.buttonLabel}
            invalid={duplicate}
          />
          {duplicate ? (
            <span className="text-xs text-error">
              {t("whatsapp.templates.manage.wizard.editor.buttonDuplicateText")}
            </span>
          ) : null}
        </label>
      </div>
    </Reorder.Item>
  );
}

function CtaRow({
  button,
  itemId,
  parameterFormat,
  phoneTouched,
  canMoveUp,
  canMoveDown,
  callingManagerUrl,
  onUpdate,
  onRemove,
  onMoveUp,
  onMoveDown,
}: {
  button: Exclude<TemplateButtonFormState, { type: "QUICK_REPLY" }>;
  itemId: string;
  parameterFormat: TemplateFormState["parameterFormat"];
  phoneTouched: boolean;
  canMoveUp: boolean;
  canMoveDown: boolean;
  callingManagerUrl: string | null;
  onUpdate: (button: TemplateButtonFormState) => void;
  onRemove: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const controls = useDragControls();
  const urlKind =
    button.type === "URL" && isDynamicTemplateUrl(button.url) ? "DYNAMIC" : "STATIC";
  const phoneInvalid =
    button.type === "PHONE_NUMBER" &&
    !isValidWhatsAppTemplatePhoneNumber(button.phoneNumber);
  const urlInvalid = button.type === "URL" && !isValidTemplateWebsiteUrl(button.url);
  const urlExampleEmpty =
    button.type === "URL" && isDynamicTemplateUrl(button.url) && !button.example?.trim();
  const durationDays =
    button.type === "VOICE_CALL"
      ? String(voiceCallTtlMinutesToDays(button.ttlMinutes ?? VOICE_CALL_TTL_MINUTES_DEFAULT))
      : "7";

  return (
    <Reorder.Item
      value={itemId}
      dragControls={controls}
      dragListener={false}
      className="rounded-md border border-line bg-surface p-3"
      layout
      transition={{ layout: { type: "spring", bounce: 0.12, visualDuration: 0.22 } }}
    >
      <RowChrome
        onPointerDown={(event) => controls.start(event)}
        onMoveUp={onMoveUp}
        onMoveDown={onMoveDown}
        onRemove={onRemove}
        canMoveUp={canMoveUp}
        canMoveDown={canMoveDown}
        removeLabel={t("whatsapp.templates.manage.wizard.editor.buttonRemove")}
        moveUpLabel={t("whatsapp.templates.manage.wizard.editor.buttonMoveUp")}
        moveDownLabel={t("whatsapp.templates.manage.wizard.editor.buttonMoveDown")}
      />

      <div className="grid gap-3 sm:grid-cols-2">
        <label className="grid gap-1 text-xs font-medium text-ink">
          {t("whatsapp.templates.manage.wizard.editor.buttonUrlType")}
          <WizardRadioSelect
            ariaLabel={t("whatsapp.templates.manage.wizard.editor.buttonUrlType")}
            value={button.type}
            onChange={() => undefined}
            disabled
            options={[
              {
                value: button.type,
                label: t(`whatsapp.templates.manage.wizard.editor.buttonTypes.${button.type}`),
              },
            ]}
          />
        </label>
        <label className="grid gap-1 text-xs font-medium text-ink">
          {t("whatsapp.templates.manage.wizard.editor.buttonUrlText")}
          <FieldCounterInput
            value={button.text}
            onChange={(text) => onUpdate({ ...button, text })}
            maxLength={TEMPLATE_LIMITS.buttonLabel}
          />
        </label>

        {button.type === "URL" ? (
          <>
            <label className="grid gap-1 text-xs font-medium text-ink">
              {t("whatsapp.templates.manage.wizard.editor.buttonUrlKind")}
              <WizardRadioSelect
                ariaLabel={t("whatsapp.templates.manage.wizard.editor.buttonUrlKind")}
                value={urlKind}
                onChange={(next) => {
                  onUpdate({
                    ...button,
                    url:
                      next === "DYNAMIC"
                        ? toDynamicTemplateUrl(button.url, parameterFormat)
                        : toStaticTemplateUrl(button.url),
                    example: next === "DYNAMIC" ? button.example : undefined,
                  });
                }}
                options={[
                  {
                    value: "STATIC",
                    label: t("whatsapp.templates.manage.wizard.editor.buttonUrlKindStatic"),
                  },
                  {
                    value: "DYNAMIC",
                    label: t("whatsapp.templates.manage.wizard.editor.buttonUrlKindDynamic"),
                  },
                ]}
              />
            </label>
            <label className="grid gap-1 text-xs font-medium text-ink">
              {t("whatsapp.templates.manage.wizard.editor.buttonUrlField")}
              <FieldCounterInput
                dir="ltr"
                value={button.url}
                onChange={(url) => onUpdate({ ...button, url })}
                maxLength={TEMPLATE_LIMITS.buttonUrl}
                placeholder={t("whatsapp.templates.manage.wizard.editor.buttonUrlPlaceholder")}
                invalid={urlInvalid}
              />
              {urlInvalid ? (
                <span className="text-xs text-error">
                  {t("whatsapp.templates.manage.wizard.editor.buttonUrlError")}
                </span>
              ) : null}
            </label>
            {urlKind === "DYNAMIC" ? (
              <label className="grid gap-1 text-xs font-medium text-ink sm:col-span-2">
                {t("whatsapp.templates.manage.wizard.editor.buttonUrlExample")}
                <FieldCounterInput
                  dir="ltr"
                  value={button.example ?? ""}
                  onChange={(example) => onUpdate({ ...button, example })}
                  maxLength={TEMPLATE_LIMITS.buttonUrl}
                  invalid={urlExampleEmpty}
                />
                {urlExampleEmpty ? (
                  <span className="text-xs text-error">
                    {t("whatsapp.templates.manage.wizard.editor.variableExampleError")}
                  </span>
                ) : null}
              </label>
            ) : null}
            <p className="sm:col-span-2 text-xs text-ink-subtle">
              {t("whatsapp.templates.manage.wizard.editor.buttonAppTrackingDisabled")}
            </p>
          </>
        ) : null}

        {button.type === "VOICE_CALL" ? (
          <div className="grid gap-2 sm:col-span-2">
            <label className="grid gap-1 text-xs font-medium text-ink">
              {t("whatsapp.templates.manage.wizard.editor.buttonVoiceCallDuration")}
              <WizardRadioSelect
                ariaLabel={t("whatsapp.templates.manage.wizard.editor.buttonVoiceCallDuration")}
                value={durationDays}
                onChange={(next) => {
                  onUpdate({
                    ...button,
                    ttlMinutes: voiceCallDaysToTtlMinutes(Number(next)),
                  });
                }}
                listClassName="max-h-[12.5rem] overflow-y-auto overscroll-contain"
                options={VOICE_CALL_DURATION_OPTIONS.map((days) => ({
                  value: days,
                  label: `${days} ${t("whatsapp.templates.manage.wizard.editor.buttonVoiceCallDays")}`,
                }))}
              />
            </label>
            <p className="m-0 text-xs leading-relaxed text-ink-muted">
              {t("whatsapp.templates.manage.wizard.editor.buttonVoiceCallEnableHint")}
            </p>
            <p className="m-0 text-xs text-ink-muted">
              {t("whatsapp.templates.manage.wizard.editor.buttonVoiceCallApiHint")}
            </p>
            <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs">
              {callingManagerUrl ? (
                <a
                  href={callingManagerUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-medium text-accent hover:underline"
                >
                  {t("whatsapp.templates.manage.wizard.editor.buttonVoiceCallManagerLink")}
                </a>
              ) : (
                <span className="text-ink-subtle">
                  {t("whatsapp.templates.manage.wizard.editor.buttonVoiceCallManagerMissing")}
                </span>
              )}
              <a
                href={WHATSAPP_CALLING_DOCS_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium text-accent hover:underline"
              >
                {t("whatsapp.templates.manage.wizard.editor.buttonVoiceCallDocsLink")}
              </a>
            </div>
          </div>
        ) : null}

        {button.type === "PHONE_NUMBER" ? (
          <div className="sm:col-span-2">
            <WizardPhoneInput
              value={button.phoneNumber}
              invalid={phoneInvalid}
              touched={phoneTouched}
              onChange={(phoneNumber) => onUpdate({ ...button, phoneNumber })}
            />
          </div>
        ) : null}
      </div>
    </Reorder.Item>
  );
}

export function MarketingDefaultButtonsEditor({
  formState,
  onChange,
  callingManagerUrl = null,
}: {
  formState: TemplateFormState;
  onChange: (buttons: TemplateButtonFormState[]) => void;
  callingManagerUrl?: string | null;
}): React.JSX.Element {
  const { t } = useTranslation();
  const [addOpen, setAddOpen] = useState(false);
  const [phoneTouched, setPhoneTouched] = useState<ReadonlySet<string>>(new Set());
  const { quickReplies, ctas } = partitionTemplateButtons(formState.buttons);
  const duplicates = duplicateQuickReplyIndexes(formState.buttons);
  const quickIds = quickReplies.map((button, index) =>
    buttonClientId(button, `qr-${index}`),
  );
  const ctaIds = ctas.map((button, index) => buttonClientId(button, `cta-${index}`));

  function replaceButtons(next: TemplateButtonFormState[]): void {
    onChange(next);
  }

  function addButton(type: TemplateButtonFormState["type"]): void {
    setAddOpen(false);
    const clientId = createTemplateFormButtonClientId();
    switch (type) {
      case "QUICK_REPLY":
        replaceButtons([
          ...formState.buttons,
          { type, clientId, text: "Quick Reply" },
        ]);
        break;
      case "URL":
        replaceButtons([
          ...formState.buttons,
          { type, clientId, text: "Visit website", url: "https://www.example.com" },
        ]);
        break;
      case "PHONE_NUMBER":
        replaceButtons([
          ...formState.buttons,
          { type, clientId, text: "Call phone number", phoneNumber: "+1" },
        ]);
        break;
      case "VOICE_CALL":
        replaceButtons([
          ...formState.buttons,
          {
            type,
            clientId,
            text: "Call on WhatsApp",
            ttlMinutes: VOICE_CALL_TTL_MINUTES_DEFAULT,
          },
        ]);
        break;
      default: {
        const _exhaustive: never = type;
        return _exhaustive;
      }
    }
  }

  function updateByClientId(clientId: string, next: TemplateButtonFormState): void {
    replaceButtons(
      formState.buttons.map((button, index) =>
        buttonClientId(button, `b-${index}`) === clientId ? { ...next, clientId } : button,
      ),
    );
    if (next.type === "PHONE_NUMBER") {
      setPhoneTouched((current) => new Set(current).add(clientId));
    }
  }

  function removeByClientId(clientId: string): void {
    replaceButtons(
      formState.buttons.filter(
        (button, index) => buttonClientId(button, `b-${index}`) !== clientId,
      ),
    );
  }

  return (
    <div className="grid gap-4">
      <Popover open={addOpen} onOpenChange={setAddOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            className="w-fit gap-1.5"
            disabled={formState.buttons.length >= 10}
          >
            <Plus size={14} />
            {t("whatsapp.templates.manage.wizard.editor.addButton")}
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-[min(22rem,calc(100vw-2rem))] p-1">
          {ADD_BUTTON_TYPES.map((type) => {
            const supported = isSupportedAddType(type, formState.category);
            const hasPhone = formState.buttons.some((button) => button.type === "PHONE_NUMBER");
            const hasVoiceCall = formState.buttons.some((button) => button.type === "VOICE_CALL");
            const usedPhone = type === "PHONE_NUMBER" && hasPhone;
            const usedCall = type === "VOICE_CALL" && hasVoiceCall;
            const blockedByOtherCallType =
              (type === "VOICE_CALL" && hasPhone) || (type === "PHONE_NUMBER" && hasVoiceCall);
            const usedUrls =
              type === "URL"
                ? formState.buttons.filter((button) => button.type === "URL").length >= 2
                : false;
            const maxOneReached = usedPhone || usedCall;
            const disabled =
              !supported ||
              usedPhone ||
              usedCall ||
              blockedByOtherCallType ||
              usedUrls ||
              formState.buttons.length >= 10;
            const reason = !supported
              ? type === "FLOW"
                ? t("whatsapp.templates.manage.wizard.editor.buttonDisabledFlow")
                : type === "SHARE_CONTACT"
                  ? t("whatsapp.templates.manage.wizard.editor.buttonDisabledShareContact")
                  : t("whatsapp.templates.manage.wizard.editor.mediaComingSoon")
              : blockedByOtherCallType
                ? type === "VOICE_CALL"
                  ? t(
                      "whatsapp.templates.manage.wizard.editor.buttonCallConflictPhoneBlocksVoice",
                    )
                  : t(
                      "whatsapp.templates.manage.wizard.editor.buttonCallConflictVoiceBlocksPhone",
                    )
              : maxOneReached
                ? t("whatsapp.templates.manage.wizard.editor.buttonMaxOne")
                : usedUrls
                  ? t("whatsapp.templates.manage.wizard.editor.buttonMaxTwoUrls")
                  : null;
            const Icon = ADD_BUTTON_ICONS[type];

            return (
              <button
                key={type}
                type="button"
                disabled={disabled}
                className="flex w-full items-start gap-2.5 rounded-md px-3 py-2 text-start text-sm hover:bg-surface-muted disabled:cursor-not-allowed disabled:opacity-50"
                onClick={() => {
                  if (!disabled && supported) addButton(type);
                }}
              >
                <Icon size={15} className="mt-0.5 shrink-0 text-ink-muted" aria-hidden="true" />
                <span className="grid min-w-0 flex-1 gap-0.5">
                  <span>{t(`whatsapp.templates.manage.wizard.editor.buttonTypes.${type}`)}</span>
                  {reason ? (
                    <span className="text-[0.65rem] leading-snug text-ink-subtle">{reason}</span>
                  ) : null}
                </span>
              </button>
            );
          })}
        </PopoverContent>
      </Popover>

      {quickReplies.length > 0 ? (
        <div className="grid gap-2">
          <h4 className="m-0 text-sm font-semibold text-ink">
            {t("whatsapp.templates.manage.wizard.editor.buttonQuickReplyTitle")}
            <span className="ms-1 font-normal text-ink-subtle">
              • {t("whatsapp.templates.manage.wizard.editor.optional")}
            </span>
          </h4>
          <Reorder.Group
            axis="y"
            values={quickIds}
            onReorder={(nextIds) =>
              replaceButtons(
                reorderGroupByClientIds(formState.buttons, "QUICK_REPLY", nextIds),
              )
            }
            className="grid gap-2"
          >
            {quickReplies.map((button, index) => {
              if (button.type !== "QUICK_REPLY") return null;
              const itemId = quickIds[index]!;
              const formIndex = formState.buttons.findIndex(
                (candidate, candidateIndex) =>
                  buttonClientId(candidate, `b-${candidateIndex}`) === itemId,
              );
              return (
                <QuickReplyRow
                  key={itemId}
                  itemId={itemId}
                  button={button}
                  duplicate={formIndex >= 0 && duplicates.has(formIndex)}
                  canMoveUp={index > 0}
                  canMoveDown={index < quickReplies.length - 1}
                  onUpdate={(next) => updateByClientId(itemId, next)}
                  onRemove={() => removeByClientId(itemId)}
                  onMoveUp={() =>
                    replaceButtons(moveGroupedButton(formState.buttons, itemId, -1))
                  }
                  onMoveDown={() =>
                    replaceButtons(moveGroupedButton(formState.buttons, itemId, 1))
                  }
                />
              );
            })}
          </Reorder.Group>
        </div>
      ) : null}

      {ctas.length > 0 ? (
        <div className="grid gap-2">
          <h4 className="m-0 text-sm font-semibold text-ink">
            {t("whatsapp.templates.manage.wizard.editor.buttonUrlTitle")}
            <span className="ms-1 font-normal text-ink-subtle">
              • {t("whatsapp.templates.manage.wizard.editor.optional")}
            </span>
          </h4>
          <Reorder.Group
            axis="y"
            values={ctaIds}
            onReorder={(nextIds) =>
              replaceButtons(reorderGroupByClientIds(formState.buttons, "CTA", nextIds))
            }
            className="grid gap-2"
          >
            {ctas.map((button, index) => {
              if (button.type === "QUICK_REPLY") return null;
              const itemId = ctaIds[index]!;
              return (
                <CtaRow
                  key={itemId}
                  itemId={itemId}
                  button={button}
                  parameterFormat={formState.parameterFormat}
                  phoneTouched={phoneTouched.has(itemId)}
                  canMoveUp={index > 0}
                  canMoveDown={index < ctas.length - 1}
                  callingManagerUrl={callingManagerUrl}
                  onUpdate={(next) => updateByClientId(itemId, next)}
                  onRemove={() => removeByClientId(itemId)}
                  onMoveUp={() =>
                    replaceButtons(moveGroupedButton(formState.buttons, itemId, -1))
                  }
                  onMoveDown={() =>
                    replaceButtons(moveGroupedButton(formState.buttons, itemId, 1))
                  }
                />
              );
            })}
          </Reorder.Group>
        </div>
      ) : null}
    </div>
  );
}
